import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BillingInformations } from '@app/shared/models/billingInformations';
import { Invoice } from '@app/shared/models/invoice';
import { Payment } from '@app/shared/models/payment';
import { PaymentIntent } from '@app/shared/models/paymentIntent';
import { PaymentMethod } from '@app/shared/models/paymentMethod';
import { Plan } from '@app/shared/models/plan';
import { PlanGroup } from '@app/shared/models/planGroup';
import { SetupIntent } from '@app/shared/models/setupIntent';
import { Subscription } from '@app/shared/models/subscription';
import { SubscriptionLimits } from '@app/shared/models/subscriptionLimits';
import { SubscriptionWithPlan } from '@app/shared/models/subscriptionWithPlan';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BillingService {

  headers = new HttpHeaders().set('Authorization', 'true');

  constructor(private http: HttpClient) { }

  getInformations(): Observable<{ informations: BillingInformations }> {
    return this.http.get<{ informations: BillingInformations }>('service:billing' + '/informations', { headers: this.headers });
  }

  updateInformations(
    data: {
      city: string,
      state?: string,
      stateCode?: string,
      country: string,
      zipCode: string,
      company?: string,
      address: string,
      lastName: string,
      firstName: string,
      vatNumber?: string,
      countryCode: string,
    }
  ): Observable<{ informations: BillingInformations }> {
    return this.http.put<{ informations: BillingInformations }>('service:billing' + '/informations', data, { headers: this.headers });
  }

  getPlan(id: string): Observable<{ plan: Plan }> {
    return this.http.get<{ plan: Plan }>('service:billing' + '/plan/' + id, { headers: this.headers });
  }

  getPlanGroup(id: string): Observable<{ group: PlanGroup }> {
    return this.http.get<{ group: PlanGroup }>('service:billing' + '/plan/' + id + '/group', { headers: this.headers });
  }

  getGroup(id: string): Observable<{ group: PlanGroup }> {
    return this.http.get<{ group: PlanGroup }>('service:billing' + '/group/' + id, { headers: this.headers });
  }

  initiateStripePayment(): Observable<{ paymentIntent: PaymentIntent }> {
    return this.http.post<{ paymentIntent: PaymentIntent }>('service:billing' + '/stripe/payment/initiate', {}, { headers: this.headers });
  }

  executeStripePayment(paymentIntent: string): Observable<{ payment: Payment }> {
    return this.http.post<{ payment: Payment }>('service:billing' + '/stripe/payment/' + paymentIntent + '/execute', {}, { headers: this.headers });
  }

  initiatePaypalPayment(): Observable<{ approvalUrl: string }> {
    return this.http.get<{ approvalUrl: string }>('service:billing' + '/paypal/payment/initiate', { headers: this.headers });
  }

  executePaypalPayment(token: string): Observable<{ payment: Payment }> {
    return this.http.post<{ payment: Payment }>('service:billing' + '/paypal/payment/' + token + '/execute', {}, { headers: this.headers });
  }

  createSubscription(plan?: string): Observable<{ subscription: Subscription }> {
    return this.http.post<{ subscription: Subscription }>('service:billing' + '/subscription', { plan }, { headers: this.headers });
  }

  deleteSubscription(): Observable<{}> {
    return this.http.delete<{}>('service:billing' + '/subscription', { headers: this.headers });
  }

  getSubscription(): Observable<{ subscription: Subscription }> {
    return this.http.get<{ subscription: Subscription }>('service:billing' + '/subscription', { headers: this.headers });
  }

  getSubscriptionWithPlan(): Observable<{ subscription: SubscriptionWithPlan }> {
    return this.http.get<{ subscription: SubscriptionWithPlan }>('service:billing' + '/subscription?version=07-2021', { headers: this.headers });
  }

  listSubscriptionLimits
  (): Observable<{ limits: SubscriptionLimits }> {
    return this.http.get<{ limits: SubscriptionLimits }>('service:billing' + '/limits?version=01-2022', { headers: this.headers });
  }

  listInvoices(): Observable<{ invoices: Invoice[] }> {
    return this.http.get<any>('service:billing' + '/invoices', { headers: this.headers });
  }

  unsubscribe(
    data: {
      reasonType?: string;
      description?: string;
    }
  ): Observable<{ subscription: Subscription }> {
    return this.http.put<{ subscription: Subscription }>('service:billing' + '/subscription/unsubscribe', data, { headers: this.headers });
  }

  retrieveStripePaymentMethod(): Observable<{ paymentMethod: PaymentMethod }> {
    return this.http.get<{ paymentMethod: PaymentMethod }>('service:billing' + '/stripe/paymentMethod', { headers: this.headers });
  }

  createStripeSetupIntent(): Observable<{ setupIntent: SetupIntent }> {
    return this.http.post<{ setupIntent: SetupIntent }>('service:billing' + '/stripe/setupIntent', {}, { headers: this.headers });
  }
}
